import type { OrderLineInput, OrderQuote } from '@api/b2b';
import { useEffect, useRef } from 'react';

import { defaultFreePicks, freePicksComplete } from './utils';

/**
 * Pre-fills free pieces whenever what the order earns changes (quantities edited, a scheme reached),
 * unless the picks on screen still fit it exactly. Edits to the split itself never reset it.
 * `keepFirst` leaves the first result alone: a saved order opens with the pieces it was saved with.
 */
export const useFreePickDefaults = (
    quote: OrderQuote | null,
    picks: OrderLineInput[],
    setPicks: (picks: OrderLineInput[]) => void,
    options: { enabled?: boolean; keepFirst?: boolean } = {},
): void => {
    const { enabled = true, keepFirst = false } = options;
    const entitlements = quote?.schemes.entitlements;
    const signature = JSON.stringify((entitlements ?? []).map((e) => [e.schemeCode, e.units, e.pool]));
    const seen = useRef<string | null>(null);
    useEffect(() => {
        if (!quote || !entitlements) return;
        const first = seen.current === null;
        if (seen.current === signature) return;
        seen.current = signature;
        if (!enabled || (first && keepFirst) || freePicksComplete(entitlements, picks)) return;
        setPicks(defaultFreePicks(entitlements, quote.lines));
        // Only a change in what is earned re-runs this; the picks are read as they are at that moment.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, enabled]);
};
